import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as svc from "../../api/purchaseService";
import { getApiErrorMessage } from "../../utils/apiError";

export const fetchPurchases = createAsyncThunk("purchases/fetchAll", async (params) => {
  const res = await svc.getPurchases(params);
  return res.data;
});

export const createPurchaseThunk = createAsyncThunk("purchases/create", async (data, { rejectWithValue }) => {
  try {
    await svc.createPurchase(data);
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to create purchase."));
  }
});

export const updatePurchaseThunk = createAsyncThunk("purchases/update", async ({ id, data }, { rejectWithValue }) => {
  try {
    const res = await svc.updatePurchase(id, data);
    return res.data;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to update purchase."));
  }
});

export const deletePurchaseThunk = createAsyncThunk("purchases/delete", async (id, { rejectWithValue }) => {
  try {
    await svc.deletePurchase(id);
    return id;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to delete purchase."));
  }
});

export const fetchPurchaseReturns = createAsyncThunk("purchaseReturns/fetchAll", async (params) => {
  const res = await svc.getPurchaseReturns(params);
  return res.data;
});

export const createPurchaseReturnThunk = createAsyncThunk("purchaseReturns/create", async (data, { rejectWithValue }) => {
  try {
    await svc.createPurchaseReturn(data);
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to create purchase return."));
  }
});

export const deletePurchaseReturnThunk = createAsyncThunk("purchaseReturns/delete", async (id, { rejectWithValue }) => {
  try {
    await svc.deletePurchaseReturn(id);
    return id;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to delete purchase return."));
  }
});

export const fetchOpeningStocks = createAsyncThunk("openingStock/fetchAll", async (params) => {
  const res = await svc.getOpeningStocks(params);
  return res.data;
});

export const createOpeningStockThunk = createAsyncThunk("openingStock/create", async (data, { rejectWithValue }) => {
  try {
    await svc.createOpeningStock(data);
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to create opening stock."));
  }
});

export const deleteOpeningStockThunk = createAsyncThunk("openingStock/delete", async (id, { rejectWithValue }) => {
  try {
    await svc.deleteOpeningStock(id);
    return id;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to delete opening stock."));
  }
});

const purchaseSlice = createSlice({
  name: "purchases",
  initialState: {
    list: [],
    returns: [],
    openingStock: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPurchases.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPurchases.fulfilled, (state, action) => { state.loading = false; state.list = Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchPurchases.rejected, (state, action) => { state.loading = false; state.error = action.error.message; })
      .addCase(deletePurchaseThunk.fulfilled, (state, action) => {
        state.list = state.list.filter((p) => (p.purchaseID ?? p.id) !== action.payload);
      })
      .addCase(fetchPurchaseReturns.pending, (state) => { state.loading = true; })
      .addCase(fetchPurchaseReturns.fulfilled, (state, action) => { state.loading = false; state.returns = Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchPurchaseReturns.rejected, (state) => { state.loading = false; })
      .addCase(deletePurchaseReturnThunk.fulfilled, (state, action) => {
        state.returns = state.returns.filter((r) => (r.purchaseReturnID ?? r.id) !== action.payload);
      })
      .addCase(fetchOpeningStocks.pending, (state) => { state.loading = true; })
      .addCase(fetchOpeningStocks.fulfilled, (state, action) => { state.loading = false; state.openingStock = Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchOpeningStocks.rejected, (state) => { state.loading = false; })
      .addCase(deleteOpeningStockThunk.fulfilled, (state, action) => {
        state.openingStock = state.openingStock.filter((o) => (o.openingStockID ?? o.id) !== action.payload);
      });
  },
});

export default purchaseSlice.reducer;
